import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { 
  X,
  Send,
  Bot,
  User,
  Loader,
  Sparkles,
  FileText,
  TrendingUp,
  Target,
  Lightbulb,
  Brain,
  Zap
} from 'lucide-react';
import { theme } from '../styles/theme';
import { aiApiService } from '../services/aiApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const AnimatedSparkles = styled(Sparkles)`
  animation: sparkle 2s ease-in-out infinite;
  
  @keyframes sparkle {
    0%, 100% {
      transform: scale(1) rotate(0deg);
      opacity: 0.9;
    }
    50% {
      transform: scale(1.1) rotate(180deg);
      opacity: 1;
    }
  }
`;

const FloatingButton = styled.button<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  color: white;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transition: all ${theme.transitions.base};
  z-index: ${theme.zIndex.modal - 1};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: translateY(0) scale(0.95);
  }
  
  ${({ $isOpen }) => $isOpen && `
    background: ${theme.colors.error[500]};
  `}
  
  @media (max-width: ${theme.breakpoints.sm}) {
    bottom: 16px;
    right: 16px;
    width: 48px;
    height: 48px;
  }
`;

const ChatModal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 90px;
  right: 24px;
  width: 400px;
  height: 600px;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid ${theme.colors.border};
  display: flex;
  flex-direction: column;
  z-index: ${theme.zIndex.modal};
  transform: ${({ $isOpen }) => $isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)'};
  opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
  visibility: ${({ $isOpen }) => $isOpen ? 'visible' : 'hidden'};
  transition: all ${theme.transitions.base};
  overflow: hidden;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    width: calc(100vw - 32px);
    height: 500px;
    bottom: 80px;
    right: 16px;
  }
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border};
  background: linear-gradient(135deg, ${theme.colors.primary[50]}, ${theme.colors.accent[50]});
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const BotAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`;

const BotName = styled.h3`
  margin: 0;
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
`;

const BotStatus = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const StatusDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.success[500]};
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  background: transparent;
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textMuted};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${theme.colors.error[100]};
    border-color: ${theme.colors.error[500]};
    color: ${theme.colors.error[600]};
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.backgroundSecondary};
    border-radius: ${theme.borderRadius.full};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.border};
    border-radius: ${theme.borderRadius.full};
    
    &:hover {
      background: ${theme.colors.textMuted};
    }
  }
`;

const Message = styled.div<{ $isUser: boolean }>`
  display: flex;
  gap: ${theme.spacing[3]};
  align-items: flex-start;
  flex-direction: ${({ $isUser }) => $isUser ? 'row-reverse' : 'row'};
`;

const MessageAvatar = styled.div<{ $isUser: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.full};
  background: ${({ $isUser }) => 
    $isUser 
      ? `linear-gradient(135deg, ${theme.colors.accent[500]}, ${theme.colors.primary[600]})` 
      : `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]})`
  };
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const MessageBubble = styled.div<{ $isUser: boolean; $isStreaming?: boolean }>`
  max-width: 80%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  background: ${({ $isUser }) => 
    $isUser 
      ? `linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]})` 
      : theme.colors.backgroundSecondary
  };
  color: ${({ $isUser }) => $isUser ? 'white' : theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.normal};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  white-space: pre-wrap;
  
  ${({ $isUser }) => !$isUser && `
    border: 1px solid ${theme.colors.border};
  `}
  
  ${({ $isStreaming }) => $isStreaming && `
    &::after {
      content: '';
      display: inline-block;
      width: 8px;
      height: 16px;
      margin-left: 4px;
      background: ${theme.colors.primary[500]};
      animation: blink 1s infinite;
    }
    
    @keyframes blink {
      0%, 50% { opacity: 1; }
      51%, 100% { opacity: 0; }
    }
  `}
`;

const MarkdownContent = styled.div<{ $isUser: boolean }>`
  ${({ $isUser }) => !$isUser && `
    h1, h2, h3, h4, h5, h6 {
      color: ${theme.colors.textPrimary};
      font-weight: ${theme.typography.fontWeight.semibold};
      margin: ${theme.spacing[3]} 0 ${theme.spacing[2]} 0;
      line-height: ${theme.typography.lineHeight.tight};
      
      &:first-child {
        margin-top: 0;
      }
    }
    
    h1 { font-size: ${theme.typography.fontSize.lg}; }
    h2 { font-size: ${theme.typography.fontSize.base}; }
    h3 { font-size: ${theme.typography.fontSize.sm}; font-weight: ${theme.typography.fontWeight.semibold}; }
    h4, h5, h6 { font-size: ${theme.typography.fontSize.sm}; }
    
    p {
      margin: ${theme.spacing[2]} 0;
      line-height: ${theme.typography.lineHeight.relaxed};
      
      &:first-child {
        margin-top: 0;
      }
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    ul, ol {
      margin: ${theme.spacing[2]} 0;
      padding-left: ${theme.spacing[4]};
      
      li {
        margin: ${theme.spacing[1]} 0;
        line-height: ${theme.typography.lineHeight.relaxed};
      }
      
      ul, ol {
        margin: ${theme.spacing[1]} 0;
      }
    }
    
    strong {
      font-weight: ${theme.typography.fontWeight.semibold};
      color: ${theme.colors.textPrimary};
    }
    
    em {
      font-style: italic;
    }
    
    code {
      background: ${theme.colors.primary[100]};
      color: ${theme.colors.primary[700]};
      padding: 2px 4px;
      border-radius: ${theme.borderRadius.sm};
      font-family: ${theme.typography.fontFamily.mono || 'monospace'};
      font-size: 0.9em;
    }
    
    pre {
      background: ${theme.colors.backgroundSecondary};
      border: 1px solid ${theme.colors.border};
      border-radius: ${theme.borderRadius.md};
      padding: ${theme.spacing[3]};
      margin: ${theme.spacing[2]} 0;
      overflow-x: auto;
      
      code {
        background: none;
        color: inherit;
        padding: 0;
      }
    }
    
    blockquote {
      border-left: 3px solid ${theme.colors.primary[500]};
      padding-left: ${theme.spacing[3]};
      margin: ${theme.spacing[2]} 0;
      color: ${theme.colors.textSecondary};
      font-style: italic;
    }
    
    hr {
      border: none;
      border-top: 1px solid ${theme.colors.border};
      margin: ${theme.spacing[3]} 0;
    }
    
    a {
      color: ${theme.colors.primary[600]};
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  `}
`;

const MessageTime = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing[1]};
  text-align: center;
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  max-width: 80%;
  
  .dots {
    display: flex;
    gap: 4px;
    
    span {
      width: 6px;
      height: 6px;
      border-radius: ${theme.borderRadius.full};
      background: ${theme.colors.textMuted};
      animation: typing 1.4s infinite ease-in-out;
      
      &:nth-child(1) { animation-delay: 0ms; }
      &:nth-child(2) { animation-delay: 200ms; }
      &:nth-child(3) { animation-delay: 400ms; }
    }
  }
  
  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.4;
    }
    30% {
      transform: translateY(-10px);
      opacity: 1;
    }
  }
`;

const InputContainer = styled.div`
  padding: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
`;

const InputForm = styled.form`
  display: flex;
  gap: ${theme.spacing[2]};
  align-items: flex-end;
`;

const MessageInput = styled.textarea`
  flex: 1;
  padding: ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  resize: none;
  min-height: 40px;
  max-height: 120px;
  transition: all ${theme.transitions.base};
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: ${theme.shadows.focus};
  }
  
  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.lg};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.accent[600]});
  color: white;
  border: none;
  cursor: pointer;
  transition: all ${theme.transitions.base};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const QuickActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[3]};
`;

const QuickActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: transparent;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.full};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.xs};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  &:hover {
    background: ${theme.colors.primary[100]};
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[700]};
  }
`;

interface ChatMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  isStreaming?: boolean;
}

const TenderBankAssistant: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      content: 'Hello! I\'m your AI Research Assistant for the Tender Bank. I can help you analyze documents, find patterns, and answer questions about your tender data. What would you like to explore?',
      isUser: false,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingMessageRef = useRef<string>('');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const handleSend = async (message?: string) => {
    const messageToSend = message || inputValue.trim();
    if (!messageToSend || isStreaming) return;

    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: messageToSend,
      isUser: true,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setIsStreaming(true);

    // Create placeholder AI message for streaming
    const aiMessageId = (Date.now() + 1).toString();
    const aiMessage: ChatMessage = {
      id: aiMessageId,
      content: '',
      isUser: false,
      timestamp: new Date(),
      isStreaming: true
    };

    setMessages(prev => [...prev, aiMessage]);
    streamingMessageRef.current = '';

    // Get chat history for context (last 10 messages excluding the current one)
    const chatHistory = messages.slice(-9).map(msg => ({
      content: msg.content,
      isUser: msg.isUser
    }));

    // Add current user message to history
    chatHistory.push({ content: messageToSend, isUser: true });

    // Convert to AI API format
    const aiMessages = aiApiService.formatMessages(chatHistory);

    try {
      await aiApiService.streamChatWithMCP(aiMessages, {
        onChunk: (chunk: string) => {
          streamingMessageRef.current += chunk;
          
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: streamingMessageRef.current }
              : msg
          ));
        },
        onComplete: () => {
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, isStreaming: false }
              : msg
          ));
          setIsTyping(false);
          setIsStreaming(false);
        },
        onError: (error: Error) => {
          console.error('OpenAI streaming error:', error);
          
          // Fallback to local response if OpenAI fails
          const fallbackResponse = generateAIResponse(messageToSend);
          
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { 
                  ...msg, 
                  content: `I apologize, but I'm having trouble connecting to the AI service. Here's a basic response:\n\n${fallbackResponse}`,
                  isStreaming: false 
                }
              : msg
          ));
          
          setIsTyping(false);
          setIsStreaming(false);
        }
      });
    } catch (error) {
      console.error('Failed to initiate streaming:', error);
      
      // Fallback to local response
      const fallbackResponse = generateAIResponse(messageToSend);
      
      setMessages(prev => prev.map(msg => 
        msg.id === aiMessageId 
          ? { 
              ...msg, 
              content: `I'm currently offline. Here's a basic response:\n\n${fallbackResponse}`,
              isStreaming: false 
            }
          : msg
      ));
      
      setIsTyping(false);
      setIsStreaming(false);
    }
  };

  const generateAIResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();
    
    if (lowerMessage.includes('pattern') || lowerMessage.includes('success')) {
      return 'Based on your tender bank, I can see several successful patterns: 1) Strong opening statements that establish credibility, 2) Technical excellence emphasis with modern methodologies, 3) Safety commitment as a core value. The highest scoring documents (90%+) consistently use phrases like "comprehensive," "proven expertise," and "industry-leading."';
    }
    
    if (lowerMessage.includes('writing style') || lowerMessage.includes('tone')) {
      return 'Your tender bank shows a consistent professional writing style with high formality levels. Most successful documents use technical language, average 18 words per sentence, and structure content with clear sections like "Executive Summary," "Technical Approach," and "Project Management."';
    }
    
    if (lowerMessage.includes('industry') || lowerMessage.includes('sector')) {
      return 'Your tender bank covers 5 industries: Infrastructure (40%), Technology (25%), Healthcare (15%), Energy (15%), and Education (5%). Infrastructure tenders have the highest average success rate at 89%, followed by Healthcare at 85%.';
    }
    
    if (lowerMessage.includes('improve') || lowerMessage.includes('better')) {
      return 'To improve your tender responses, I recommend: 1) Use more quantifiable achievements (your high-scoring docs include specific metrics), 2) Emphasize safety and compliance early, 3) Include environmental considerations, 4) Structure responses with clear headings that match your successful patterns.';
    }
    
    if (lowerMessage.includes('score') || lowerMessage.includes('performance')) {
      return 'Your tender bank shows an average success score of 82%. Documents scoring 90%+ share common traits: strong credibility statements, detailed technical approaches, comprehensive safety protocols, and environmental commitments. Would you like me to analyze specific high-performing documents?';
    }
    
    return 'I can help you analyze your tender bank data in many ways. Try asking about success patterns, writing styles, industry performance, or how to improve your responses. You can also ask me to compare documents or find specific examples.';
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickActions = [
    { icon: <TrendingUp size={12} />, text: 'Show success patterns', message: 'What success patterns can you find in my tender bank?' },
    { icon: <FileText size={12} />, text: 'Analyze writing style', message: 'Analyze the writing style of my successful tenders' },
    { icon: <Target size={12} />, text: 'Industry performance', message: 'How do my tenders perform across different industries?' },
    { icon: <Lightbulb size={12} />, text: 'Improvement tips', message: 'How can I improve my tender responses?' }
  ];

  return (
    <>
      <FloatingButton $isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <X size={24} /> : <AnimatedSparkles size={24} />}
      </FloatingButton>

      <ChatModal $isOpen={isOpen}>
        <ChatHeader>
          <HeaderContent>
            <BotAvatar>
              <Bot size={20} />
            </BotAvatar>
            <HeaderText>
              <BotName>AI Assistant</BotName>
              <BotStatus>
                <StatusDot />
                Online • Tender Bank Expert
              </BotStatus>
            </HeaderText>
          </HeaderContent>
          <CloseButton onClick={() => setIsOpen(false)}>
            <X size={16} />
          </CloseButton>
        </ChatHeader>

        <MessagesContainer>
          {messages.map((message) => (
            <Message key={message.id} $isUser={message.isUser}>
              <MessageAvatar $isUser={message.isUser}>
                {message.isUser ? <User size={16} /> : <Bot size={16} />}
              </MessageAvatar>
              <div>
                <MessageBubble $isUser={message.isUser} $isStreaming={message.isStreaming}>
                  {message.isUser ? (
                    message.content
                  ) : (
                    <MarkdownContent $isUser={message.isUser}>
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          // Custom components to handle streaming cursor
                          p: ({ children }) => <p>{children}</p>,
                          strong: ({ children }) => <strong>{children}</strong>,
                          em: ({ children }) => <em>{children}</em>,
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </MarkdownContent>
                  )}
                </MessageBubble>
                <MessageTime>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </MessageTime>
              </div>
            </Message>
          ))}

          {isTyping && (
            <Message $isUser={false}>
              <MessageAvatar $isUser={false}>
                <Bot size={16} />
              </MessageAvatar>
              <TypingIndicator>
                <div className="dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                AI is typing...
              </TypingIndicator>
            </Message>
          )}

          {messages.length === 1 && (
            <QuickActions>
              {quickActions.map((action, index) => (
                <QuickActionButton
                  key={index}
                  onClick={() => handleSend(action.message)}
                >
                  {action.icon}
                  {action.text}
                </QuickActionButton>
              ))}
            </QuickActions>
          )}

          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputContainer>
          <InputForm onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
            <MessageInput
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me about your tender bank..."
              disabled={isTyping || isStreaming}
            />
            <SendButton 
              type="submit" 
              disabled={!inputValue.trim() || isTyping || isStreaming}
            >
              {(isTyping || isStreaming) ? <Loader className="animate-spin" size={16} /> : <Send size={16} />}
            </SendButton>
          </InputForm>
        </InputContainer>
      </ChatModal>
    </>
  );
};

export default TenderBankAssistant;